import { cn } from "cy-fe/src/utils/cn";
import { assert } from "cy-util";

export interface Step {
  label: string;
  description: string;
}
export interface StepperProps {
  currentStep: number;
  steps: Step[] | readonly Step[];
  className?: string;
  "data-testid"?: string;
}
const dynamicStyles: Record<number, string> = {
  2: "grid-cols-2",
  3: "grid-cols-3",
  4: "grid-cols-4",
  5: "grid-cols-5",
  6: "grid-cols-6",
} as const;
export function Stepper({
  currentStep,
  steps,
  className,
  "data-testid": dataTestId,
}: StepperProps) {
  if (steps.length === 0) return null;
  const baseTestId = dataTestId || "stepper";
  const stepWidth = 1 / (steps.length - 1);
  const percentage =
    currentStep === 1
      ? 0.03
      : currentStep >= steps.length
        ? 1
        : stepWidth * (currentStep - 1);
  assert(
    currentStep <= steps.length,
    `currentStep ${currentStep} is greater than steps length ${steps.length}`,
  );
  return (
    <div data-testid={baseTestId} className={cn("w-full", className)}>
      <h4 className="sr-only">Status</h4>
      <p
        className="ml-[10%] text-sm font-medium text-gray-900"
        data-testid={`${baseTestId}-current-description`}
      >
        <span className="inline">{steps[currentStep - 1].description}</span>
      </p>
      <div aria-hidden="true" className="mt-6">
        <div
          className="mx-auto w-[80%] overflow-hidden rounded-full bg-gray-200"
          data-testid={`${baseTestId}-progress-bar`}
        >
          <div
            style={{ width: `${percentage * 100}%` }}
            className="h-2 rounded-full bg-black"
            data-testid={`${baseTestId}-progress-fill`}
          />
        </div>
        <div
          className={cn(
            "mt-6 hidden text-sm font-medium text-gray-600 sm:grid",
            dynamicStyles[steps.length],
          )}
          data-testid={`${baseTestId}-steps`}
        >
          {steps.map((step, index) => (
            <div
              key={index}
              data-testid={`${baseTestId}-step-${index}`}
              className={cn(
                index + 1 <= currentStep && "text-black",
                "!text-center",
                index > 0 && index < steps.length - 1 && "text-center",
                index === steps.length - 1 && "text-right",
              )}
            >
              {step.label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
